import { Feature } from "geojson";
import { pick } from "lodash";
import { MapMouseEvent } from "mapbox-gl";
import { useEffect } from "react";

import { useGaiaMap } from "../../hooks/useGaiaMap";
import { parseStringifiedProperties } from "./parseStringifiedProperties";

type GaiaMapFeatureHoverListenerProps<T> = {
    onHoverCallback: (feature: T | undefined, layerId?: string) => void;
    onHoverEndCallback?: (feature?: T | undefined, layerId?: string) => void;
    layerIds: string[];
};

export function GaiaMapFeatureHoverListener<T = Feature>({
    onHoverCallback,
    onHoverEndCallback,
    layerIds,
}: GaiaMapFeatureHoverListenerProps<T>) {
    const { map } = useGaiaMap();

    useEffect(() => {
        if (!map) return;

        function processFeature(feature: Feature): T {
            const pickedFeature = pick(feature, ["id", "type", "bbox", "geometry", "properties"]);

            if (pickedFeature.properties) {
                pickedFeature.properties = parseStringifiedProperties(pickedFeature.properties);
            }

            return pickedFeature as T;
        }

        function handleHover(event: MapMouseEvent) {
            if (!map || !event.point) return;

            if (event.originalEvent.target !== map.getCanvas()) return;

            const feature = event.features?.[0];

            if (feature) {
                onHoverCallback(processFeature(feature), feature.layer?.id);
            }
        }

        function handleHoverEnd(event: MapMouseEvent) {
            if (!map || !onHoverEndCallback) return;

            const feature = event.features?.[0];

            if (feature) {
                onHoverEndCallback(processFeature(feature), feature.layer?.id);
            } else {
                onHoverEndCallback();
            }
        }

        layerIds.forEach(layerId => {
            map.on("mousemove", layerId, handleHover);
            if (onHoverEndCallback) {
                map.on("mouseleave", layerId, handleHoverEnd);
            }
        });

        return () => {
            if (!map) return;

            layerIds.forEach(layerId => {
                map.off("mousemove", layerId, handleHover);
                if (onHoverEndCallback) {
                    map.off("mouseleave", layerId, handleHoverEnd);
                }
            });
        };
    }, [map, onHoverCallback, onHoverEndCallback, layerIds]);

    return null;
}
